/**
 * AK-584 — «Настройка не закончена»: янтарная полоса под шапкой, пока в
 * тенанте нет ни одного поля.
 *
 * Живёт ровно до появления первого поля и исчезает сама — отдельного «я всё
 * настроил» нажимать не нужно. Крестик прячет полосу до конца сессии
 * (sessionStorage), а не навсегда: человек, который «зашёл посмотреть»,
 * не должен потерять дорогу назад совсем.
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { CircleDashed, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { isSetupIncomplete } from '@/lib/onboarding/setupState';
import { useSetupStatus } from '@/lib/onboarding/useSetupStatus';
import { useCan, PERM } from '@/lib/auth/usePermissions';

const DISMISS_KEY = 'ikolhoz.setup_banner_dismissed';

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function SetupIncompleteBanner() {
  const navigate = useNavigate();
  const { counts, isError } = useSetupStatus();
  // Звать «продолжить настройку» имеет смысл только того, кто может завести
  // поле. Роль без права создавать поля (и режим тех.поддержки, где доступ
  // может быть read-only) получала бы призыв к действию, которое ей запрещено.
  const canCreateField = useCan(PERM.fieldWrite, 'c');
  const [dismissed, setDismissed] = useState(readDismissed);

  // Состав тенанта неизвестен (ручка упала) — молчим, а не объявляем систему
  // ненастроенной.
  if (isError || dismissed || !canCreateField || !isSetupIncomplete(counts)) {
    return null;
  }

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // приватный режим / переполнение — переживём, скроем только на этот раз
    }
    setDismissed(true);
  };

  return (
    <Alert
      variant="warning"
      data-testid="setup-incomplete-banner"
      className="flex items-center justify-between gap-3 rounded-none border-x-0 border-t-0 px-4 py-2"
    >
      <div className="flex min-w-0 items-center gap-2">
        <CircleDashed className="h-4 w-4 shrink-0" />
        <AlertDescription className="text-sm leading-tight">
          <strong className="font-semibold">Настройка не закончена</strong> — в
          системе пока нет ни одного поля.
        </AlertDescription>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          size="sm"
          className="h-7"
          data-testid="setup-incomplete-continue"
          onClick={() => navigate({ to: '/getting-started' })}
        >
          Продолжить настройку
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0"
          aria-label="Скрыть напоминание о настройке"
          onClick={dismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </Alert>
  );
}

export default SetupIncompleteBanner;
